import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  House, Storefront, Sparkle, ShieldCheck, CalendarBlank, Receipt, ArrowRight, X,
} from "@phosphor-icons/react";

/**
 * Iter209aq — Onboarding "prime 3 aree" per pro master standard.
 *
 * Wizard a 4 step con progress bar, mostrato una sola volta al primo
 * accesso, subito dopo `subscription_onboarding_completed`.
 * Al termine (o via "Esplora la dashboard") viene chiamato
 * POST /users/me/onboarding-complete per marcare
 * `onboarding_completed = true` sul profilo.
 */
export default function ProOnboardingModal() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const isEligible = useMemo(() => (
    !!user
    && user.role === "professional"
    && !user.parent_user_id
    && user.subscription_onboarding_completed === true
    && user.onboarding_completed === false
  ), [user]);

  if (!isEligible) return null;

  const markCompleted = async () => {
    try {
      await api.post("/users/me/onboarding-complete");
      await refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il salvataggio");
    }
  };

  const handleAddProperty = async () => {
    setBusy(true);
    await markCompleted();
    setBusy(false);
    navigate("/properties?new=1");
  };

  const handleExplore = async () => {
    // Iter209bp — "Chiudi tour": marca onboarding_completed e porta
    // l'utente alla dashboard SENZA avviare il driver.js tour (che
    // resta disponibile su richiesta ma non parte in automatico).
    setBusy(true);
    await markCompleted();
    setBusy(false);
    navigate("/dashboard");
  };

  const firstName = (user.first_name || "").split(" ")[0] || "Pro";
  const steps = [
    <WelcomeStep key="s1" name={firstName} onNext={() => setStep(1)} />,
    <AreasStep key="s2" onNext={() => setStep(2)} />,
    <PlafondStep key="s3" onNext={() => setStep(3)} />,
    <FirstActionStep
      key="s4"
      busy={busy}
      onAdd={handleAddProperty}
      onExplore={handleExplore}
    />,
  ];

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="pro-onboarding-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200 my-8">
        <ProgressBar step={step} total={steps.length} />
        <div className="p-6 sm:p-10">{steps[step]}</div>
      </div>
    </div>
  );
}

function ProgressBar({ step, total }) {
  const pct = Math.round(((step + 1) / total) * 100);
  return (
    <div className="w-full">
      <div className="flex items-center justify-between px-6 sm:px-10 pt-6 pb-2">
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
          <span data-testid="pro-onboarding-step-indicator">Passo {step + 1} di {total}</span>
        </div>
        <div className="text-xs text-slate-400">{pct}%</div>
      </div>
      <div className="h-1.5 w-full bg-slate-100">
        <div
          className="h-full bg-purple-600 transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="pro-onboarding-progress"
        />
      </div>
    </div>
  );
}

// ---------- STEP 1: Benvenuto ----------
function WelcomeStep({ name, onNext }) {
  return (
    <div className="text-center space-y-5" data-testid="pro-onboarding-step-welcome">
      <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto">
        <Sparkle size={30} weight="fill" className="text-purple-600" />
      </div>
      <h2 className="font-display text-3xl font-semibold text-slate-900">
        Ciao {name}, benvenuto in Housetag.
      </h2>
      <p className="text-slate-600 text-base max-w-lg mx-auto leading-relaxed">
        Hai tutto quello che ti serve per gestire i tuoi immobili e accedere ai migliori servizi tecnici.
      </p>
      <Button
        onClick={onNext}
        className="bg-purple-600 hover:bg-purple-700 text-white px-8 h-11 rounded-full mt-4"
        data-testid="pro-onboarding-welcome-next"
      >
        Iniziamo <ArrowRight size={16} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}

// ---------- STEP 2: Le 3 aree principali ----------
function AreasStep({ onNext }) {
  const cards = [
    { icon: House, title: "Immobili", desc: "Carica e gestisci il tuo portafoglio immobiliare in un unico posto.", tid: "pro-onboarding-area-properties" },
    { icon: Storefront, title: "Marketplace", desc: "Acquista servizi tecnici per i tuoi immobili: visure, accessi agli atti, perizie e molto altro.", tid: "pro-onboarding-area-marketplace" },
    { icon: Sparkle, title: "Agente AI", desc: "Genera contratti e documenti professionali in pochi secondi direttamente dalla scheda immobile.", tid: "pro-onboarding-area-ai" },
  ];
  return (
    <div className="space-y-6" data-testid="pro-onboarding-step-areas">
      <div className="text-center space-y-2">
        <h2 className="font-display text-2xl font-semibold text-slate-900">Le tue tre aree principali</h2>
        <p className="text-sm text-slate-500">Tutto quello che puoi fare, da subito.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div
            key={c.title}
            data-testid={c.tid}
            className="rounded-2xl border border-slate-200 bg-slate-50 hover:border-purple-300 hover:bg-white transition p-4 cursor-default"
          >
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mb-3">
              <c.icon size={20} weight="fill" className="text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-900 text-sm mb-1">{c.title}</h3>
            <p className="text-xs text-slate-600 leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-center pt-2">
        <Button
          onClick={onNext}
          className="bg-purple-600 hover:bg-purple-700 text-white px-8 h-11 rounded-full"
          data-testid="pro-onboarding-areas-next"
        >
          Continua <ArrowRight size={16} weight="bold" className="ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ---------- STEP 3: Come funziona il plafond ----------
function PlafondStep({ onNext }) {
  const timeline = [
    { icon: ShieldCheck, label: "Oggi", text: "richiedi il servizio", tid: "pro-onboarding-timeline-today" },
    { icon: CalendarBlank, label: "Fine mese", text: "riepilogo", tid: "pro-onboarding-timeline-endmonth" },
    { icon: Receipt, label: "1° del mese", text: "fatturazione", tid: "pro-onboarding-timeline-invoice" },
  ];
  return (
    <div className="space-y-6" data-testid="pro-onboarding-step-plafond">
      <div className="text-center space-y-2">
        <h2 className="font-display text-2xl font-semibold text-slate-900">Acquista ora, paghi dopo.</h2>
        <p className="text-slate-600 max-w-lg mx-auto text-sm leading-relaxed">
          Con Housetag puoi richiedere servizi oggi senza pagare subito. Tutti i servizi che utilizzi vengono raccolti e ti verrà inviato un riepilogo all'inizio del mese successivo. Nessun pagamento immediato, nessuna sorpresa.
        </p>
      </div>
      <div className="relative">
        <div className="hidden sm:block absolute top-6 left-[16%] right-[16%] h-0.5 bg-purple-200" aria-hidden />
        <div className="grid grid-cols-3 gap-2 relative">
          {timeline.map((t, i) => (
            <div key={t.label} className="flex flex-col items-center text-center" data-testid={t.tid}>
              <div className="w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center relative z-10 mb-2">
                <t.icon size={20} weight="fill" />
              </div>
              <div className="text-xs font-semibold text-slate-900">{t.label}</div>
              <div className="text-xs text-slate-500">{t.text}</div>
              {i < timeline.length - 1 && (
                <div className="sm:hidden text-purple-400 mt-1">↓</div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center pt-2">
        <Button
          onClick={onNext}
          className="bg-purple-600 hover:bg-purple-700 text-white px-8 h-11 rounded-full"
          data-testid="pro-onboarding-plafond-next"
        >
          Continua <ArrowRight size={16} weight="bold" className="ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ---------- STEP 4: Prima azione ----------
function FirstActionStep({ busy, onAdd, onExplore }) {
  return (
    <div className="text-center space-y-5" data-testid="pro-onboarding-step-first-action">
      <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto">
        <House size={30} weight="fill" className="text-purple-600" />
      </div>
      <h2 className="font-display text-2xl font-semibold text-slate-900">
        Inizia aggiungendo il tuo primo immobile.
      </h2>
      <p className="text-sm text-slate-500">Ci vorranno meno di 2 minuti.</p>
      <div className="flex flex-col items-center gap-3 pt-2">
        <Button
          onClick={onAdd}
          disabled={busy}
          className="bg-purple-600 hover:bg-purple-700 text-white px-8 h-12 rounded-full min-w-[220px]"
          data-testid="pro-onboarding-add-property"
        >
          {busy ? "Attendere…" : "Aggiungi immobile"}
        </Button>
        <button
          onClick={onExplore}
          disabled={busy}
          className="text-sm text-slate-500 hover:text-slate-800 underline underline-offset-4 disabled:opacity-50"
          data-testid="pro-onboarding-explore-link"
        >
          Chiudi tour
        </button>
      </div>
    </div>
  );
}

// Icon X viene usato in altri overlay: import di comodo per evitare warnings
void X;
